import type { FC } from 'react';

import type { AdminPaymentRecord } from '@/types/payment.types';
import { formatRupeesExact } from '@/utils/currency.util';

import type { PaymentActionForm } from '../hooks/usePaymentsPage.hook';
import {
  COLLECTION_STATUS_LABEL,
  DEPOSIT_STATUS_LABEL,
  PAYOUT_METHOD_LABEL,
  PAYOUT_MISSING_BORROWER,
  PAYOUT_MISSING_OWNER,
  PAYOUT_REMIND_LABEL,
  REFUND_CONFIRM_LABEL,
  REFUND_DUE_BANNER,
  REFUND_MISSING_PAYOUT,
  REFUND_NOT_RECEIVED_LABEL,
  REFUND_REASON_LABEL,
  REFUND_UNVERIFIED_WARNING,
  SETTLEMENT_STATUS_LABEL,
} from '../constants';

interface PaymentDetailDrawerProps {
  record: AdminPaymentRecord;
  isMutating: boolean;
  actionError: string | null;
  openForm: PaymentActionForm;
  draft: string;
  onClose: () => void;
  onOpenForm: (form: PaymentActionForm) => void;
  onDraftChange: (value: string) => void;
  onVerify: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSettle: (id: string, adminRef: string) => void;
  onRefundDeposit: (id: string, ref?: string) => void;
  onConfirmRefund: (id: string, ref?: string) => void;
  onNoPaymentReceived: (id: string, reason: string) => void;
  onRequestPayoutDetails: (id: string) => void;
  copiedKey: string | null;
  onCopy: (key: string, value: string) => void;
}

const Line: FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-[12px] text-[var(--color-ink-muted)]">{label}</span>
    <span className={`text-[12px] ${strong ? 'font-bold text-[var(--color-ink)]' : 'text-[var(--color-ink)]'}`}>
      {value}
    </span>
  </div>
);

/** One payout field with a copy button — the admin re-types none of this into their bank app. */
const CopyLine: FC<{
  label: string;
  value: string;
  fieldKey: string;
  copiedKey: string | null;
  onCopy: (key: string, value: string) => void;
}> = ({ label, value, fieldKey, copiedKey, onCopy }) => (
  <div className="flex items-center justify-between gap-2 py-1">
    <span className="shrink-0 text-[12px] text-[var(--color-ink-muted)]">{label}</span>
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate font-mono text-[12px] text-[var(--color-ink)]">{value}</span>
      <button
        type="button"
        onClick={() => onCopy(fieldKey, value)}
        className="shrink-0 rounded border border-[var(--color-divider)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
      >
        {copiedKey === fieldKey ? 'Copied' : 'Copy'}
      </button>
    </div>
  </div>
);

const secondaryBtn =
  'rounded-lg border border-[var(--color-divider)] bg-white px-3 py-2 text-[12px] font-semibold text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] disabled:opacity-50';

const PaymentDetailDrawer: FC<PaymentDetailDrawerProps> = ({
  record,
  isMutating,
  actionError,
  openForm,
  draft,
  onClose,
  onOpenForm,
  onDraftChange,
  onVerify,
  onReject,
  onSettle,
  onRefundDeposit,
  onConfirmRefund,
  onNoPaymentReceived,
  onRequestPayoutDetails,
  copiedKey,
  onCopy,
}) => {
  const a = record.amounts;
  // One chronological timeline: payment events + booking lifecycle phases.
  const timeline = [...record.timeline, ...record.bookingTimeline].sort((x, y) =>
    x.at.localeCompare(y.at),
  );
  const canVerify = record.collectionStatus === 'payment_submitted';
  // Settlement / deposit refund only after the rental is returned (readyToSettle).
  // A transfer needs a destination. Without one the button is not merely disabled — the server
  // rejects the settle too, so recording it here would be a lie either way.
  const ownerPayout = record.owner.payout;
  const borrowerPayout = record.borrower.payout;
  const canSettle =
    record.readyToSettle && record.settlementStatus === 'pending' && Boolean(ownerPayout);
  const settleBlockedOnPayout =
    record.readyToSettle && record.settlementStatus === 'pending' && !ownerPayout;
  // The pre-payout-details path: refunds collected before this feature only ever had the UPI ID
  // the borrower typed on their proof.
  const borrowerDestination = borrowerPayout ?? record.borrowerUpiId;
  const canRefund =
    record.readyToSettle &&
    record.depositRefundStatus === 'pending' &&
    a.depositPaise > 0 &&
    Boolean(borrowerDestination);
  const refundBlockedOnPayout =
    record.readyToSettle &&
    record.depositRefundStatus === 'pending' &&
    a.depositPaise > 0 &&
    !borrowerDestination;
  // The whole borrower total goes back, not just the deposit — the rental never happened.
  const refundDue = record.collectionStatus === 'refund_due';
  const canConfirmRefund = refundDue && Boolean(borrowerDestination);
  // Only an unverified record can be closed as "never arrived": verified money is in the bank.
  const canCloseAsNoPayment = refundDue && !record.paymentWasVerified;
  const awaitingReturn =
    record.collectionStatus === 'verified' && !record.readyToSettle && record.settlementStatus === 'pending';
  // A payout only unlocks when the OWNER confirms the return; the borrower marking it returned
  // moves the booking to `return_initiated` and changes nothing here. Naming the wrong party
  // made a stuck payout look like the borrower's fault.
  const awaitingReturnNote =
    record.bookingStatus === 'return_initiated'
      ? `${record.borrower.name} marked the item returned — waiting for ${record.owner.name} to confirm receipt. You can settle once they do.`
      : record.bookingStatus === 'active'
        ? `Payment verified — waiting for ${record.borrower.name} to return the item. You can settle once ${record.owner.name} confirms the return.`
        : 'Payment verified — waiting for the rental to complete. You can settle once the owner confirms the return.';

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--color-divider)] p-4">
          <div>
            <p className="font-mono text-[14px] font-bold text-[var(--color-ink)]">{record.paymentCode}</p>
            <p className="text-[11px] text-[var(--color-ink-muted)]">Booking {record.bookingCode}</p>
          </div>
          <button type="button" onClick={onClose} className={secondaryBtn}>
            Close
          </button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          <div className="flex gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${COLLECTION_STATUS_LABEL[record.collectionStatus].tone}`}
            >
              {COLLECTION_STATUS_LABEL[record.collectionStatus].label}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${SETTLEMENT_STATUS_LABEL[record.settlementStatus].tone}`}
            >
              {SETTLEMENT_STATUS_LABEL[record.settlementStatus].label}
            </span>
            {a.depositPaise > 0 ? (
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${DEPOSIT_STATUS_LABEL[record.depositRefundStatus].tone}`}
              >
                {DEPOSIT_STATUS_LABEL[record.depositRefundStatus].label}
              </span>
            ) : null}
          </div>

          <p className="text-[12px] text-[var(--color-ink)]">
            {record.borrower.name} → {record.owner.name}
            {record.campusName ? ` · ${record.campusName}` : ''}
          </p>

          {/* Breakdown */}
          <div className="rounded-xl border border-[var(--color-divider)] p-3">
            <Line label="Rental" value={formatRupeesExact(a.rentalPaise)} />
            <Line label={`Commission (${a.commissionPct}%)`} value={formatRupeesExact(a.commissionPaise)} />
            <Line label={`GST on fee (${a.gstPct}%)`} value={formatRupeesExact(a.gstOnCommissionPaise)} />
            {a.depositPaise > 0 ? <Line label="Deposit" value={formatRupeesExact(a.depositPaise)} /> : null}
            <div className="my-1 border-t border-[var(--color-divider)]" />
            <Line label="Borrower paid" value={formatRupeesExact(a.borrowerTotalPaise)} strong />
            <div className="my-1 border-t border-[var(--color-divider)]" />
            <Line label={`Settlement fee (${a.settlementFeePct}%)`} value={`−${formatRupeesExact(a.settlementFeePaise)}`} />
            <Line label="GST on settlement fee" value={`−${formatRupeesExact(a.gstOnSettlementFeePaise)}`} />
            <Line label="Owner payout" value={formatRupeesExact(a.ownerPayoutPaise)} strong />
            <Line label="GST liability" value={formatRupeesExact(a.gstLiabilityPaise)} />
          </div>

          {/* Where the payout goes — the one thing the admin needs to make the transfer */}
          {ownerPayout ? (
            <div className="rounded-xl border border-[var(--color-divider)] p-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                Send {formatRupeesExact(a.ownerPayoutPaise)} to {record.owner.name}
              </p>
              <CopyLine
                label="Holder"
                value={ownerPayout.holderName}
                fieldKey="owner-holder"
                copiedKey={copiedKey}
                onCopy={onCopy}
              />
              {ownerPayout.method === 'upi' && ownerPayout.upiId ? (
                <CopyLine
                  label="UPI ID"
                  value={ownerPayout.upiId}
                  fieldKey="owner-upi"
                  copiedKey={copiedKey}
                  onCopy={onCopy}
                />
              ) : null}
              {ownerPayout.method === 'bank' && ownerPayout.accountNumber ? (
                <>
                  <CopyLine
                    label="Account"
                    value={ownerPayout.accountNumber}
                    fieldKey="owner-account"
                    copiedKey={copiedKey}
                    onCopy={onCopy}
                  />
                  <CopyLine
                    label="IFSC"
                    value={ownerPayout.ifsc ?? ''}
                    fieldKey="owner-ifsc"
                    copiedKey={copiedKey}
                    onCopy={onCopy}
                  />
                </>
              ) : null}
              <p className="mt-1 text-[10px] text-[var(--color-ink-muted)]">
                {PAYOUT_METHOD_LABEL[ownerPayout.method]} · added{' '}
                {new Date(ownerPayout.updatedAt).toLocaleDateString()}
              </p>
            </div>
          ) : null}

          {/* Where the deposit goes back to */}
          {a.depositPaise > 0 && borrowerPayout ? (
            <div className="rounded-xl border border-[var(--color-divider)] p-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                Refund {formatRupeesExact(a.depositPaise)} to {record.borrower.name}
              </p>
              <CopyLine
                label="Holder"
                value={borrowerPayout.holderName}
                fieldKey="borrower-holder"
                copiedKey={copiedKey}
                onCopy={onCopy}
              />
              {borrowerPayout.method === 'upi' && borrowerPayout.upiId ? (
                <CopyLine
                  label="UPI ID"
                  value={borrowerPayout.upiId}
                  fieldKey="borrower-upi"
                  copiedKey={copiedKey}
                  onCopy={onCopy}
                />
              ) : null}
              {borrowerPayout.method === 'bank' && borrowerPayout.accountNumber ? (
                <>
                  <CopyLine
                    label="Account"
                    value={borrowerPayout.accountNumber}
                    fieldKey="borrower-account"
                    copiedKey={copiedKey}
                    onCopy={onCopy}
                  />
                  <CopyLine
                    label="IFSC"
                    value={borrowerPayout.ifsc ?? ''}
                    fieldKey="borrower-ifsc"
                    copiedKey={copiedKey}
                    onCopy={onCopy}
                  />
                </>
              ) : null}
            </div>
          ) : null}

          {/* Proof */}
          {record.borrowerRef || record.borrowerUpiId || record.proofUrl ? (
            <div className="rounded-xl border border-[var(--color-divider)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                Payment proof
              </p>
              {record.borrowerRef ? (
                <p className="mt-1 font-mono text-[13px] text-[var(--color-ink)]">{record.borrowerRef}</p>
              ) : null}
              {record.borrowerUpiId ? (
                <p className="mt-0.5 font-mono text-[12px] text-[var(--color-ink-muted)]">
                  from {record.borrowerUpiId}
                </p>
              ) : null}
              {record.proofUrl ? (
                <a href={record.proofUrl} target="_blank" rel="noreferrer">
                  <img
                    src={record.proofUrl}
                    alt="Payment proof"
                    className="mt-2 max-h-64 w-full rounded-lg object-contain"
                  />
                </a>
              ) : null}
            </div>
          ) : null}

          {/* Timeline */}
          <div className="rounded-xl border border-[var(--color-divider)] p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
              Timeline
            </p>
            <ol className="flex flex-col gap-2">
              {timeline.map((t, i) => (
                <li key={`${t.event}-${t.at}-${i}`} className="text-[12px] text-[var(--color-ink)]">
                  <span className="font-semibold">{t.event.replace(/_/g, ' ')}</span>
                  <span className="text-[var(--color-ink-muted)]"> · {new Date(t.at).toLocaleString()}</span>
                  {t.note ? <span className="block text-[11px] text-[var(--color-ink-muted)]">{t.note}</span> : null}
                </li>
              ))}
            </ol>
          </div>

          {actionError ? <p className="text-[12px] text-red-600">{actionError}</p> : null}

          {awaitingReturn ? (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
              {awaitingReturnNote}
            </p>
          ) : null}

          {refundDue ? (
            <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2">
              <p className="text-[12px] font-semibold text-red-700">
                Refund {formatRupeesExact(a.borrowerTotalPaise)} to {record.borrower.name}
                {record.refundReason ? ` · ${REFUND_REASON_LABEL[record.refundReason]}` : ''}
              </p>
              <p className="mt-1 text-[12px] text-red-700">{REFUND_DUE_BANNER}</p>
              {!record.paymentWasVerified ? (
                <p className="mt-1 text-[12px] font-semibold text-red-800">
                  {REFUND_UNVERIFIED_WARNING}
                </p>
              ) : null}
              {!borrowerDestination ? (
                <>
                  <p className="mt-1 text-[12px] text-red-700">{REFUND_MISSING_PAYOUT}</p>
                  <button
                    type="button"
                    disabled={isMutating}
                    onClick={() => onRequestPayoutDetails(record.id)}
                    className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    {isMutating ? 'Sending…' : PAYOUT_REMIND_LABEL}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}

          {record.collectionStatus === 'refunded' ? (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
              Refunded {formatRupeesExact(a.borrowerTotalPaise)} to {record.borrower.name}
              {record.refundRef ? ` · ref ${record.refundRef}` : ''}.
            </p>
          ) : null}

          {settleBlockedOnPayout || refundBlockedOnPayout ? (
            <div className="rounded-lg bg-amber-50 px-3 py-2">
              <p className="text-[12px] text-amber-700">
                {settleBlockedOnPayout ? PAYOUT_MISSING_OWNER : PAYOUT_MISSING_BORROWER}
              </p>
              <button
                type="button"
                disabled={isMutating}
                onClick={() => onRequestPayoutDetails(record.id)}
                className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
              >
                {isMutating ? 'Sending…' : PAYOUT_REMIND_LABEL}
              </button>
            </div>
          ) : null}

          {record.settlementStatus === 'paid' ? (
            <p className="rounded-lg bg-brand-50 px-3 py-2 text-[12px] text-brand-600">
              Payout sent — waiting for {record.owner.name} to confirm they received it.
            </p>
          ) : null}
          {record.depositRefundStatus === 'refunded' && a.depositPaise > 0 ? (
            <p className="rounded-lg bg-brand-50 px-3 py-2 text-[12px] text-brand-600">
              Deposit refunded — waiting for {record.borrower.name} to confirm they received it.
            </p>
          ) : null}

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {canVerify ? (
              <>
                <button
                  type="button"
                  disabled={isMutating}
                  onClick={() => onVerify(record.id)}
                  className="rounded-lg bg-emerald-600 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isMutating ? 'Working…' : 'Verify payment'}
                </button>
                {openForm === 'reject' ? (
                  <div className="rounded-lg border border-[var(--color-divider)] p-3">
                    <textarea
                      value={draft}
                      onChange={(e) => onDraftChange(e.target.value)}
                      placeholder="Reason (shown to the borrower)"
                      className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                      rows={2}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        disabled={isMutating || draft.trim().length < 3}
                        onClick={() => onReject(record.id, draft.trim())}
                        className="rounded-lg bg-red-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        Confirm reject
                      </button>
                      <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => onOpenForm('reject')} className={secondaryBtn}>
                    Reject payment
                  </button>
                )}
              </>
            ) : null}

            {canConfirmRefund ? (
              openForm === 'close_refund' ? (
                <div className="rounded-lg border border-[var(--color-divider)] p-3">
                  <input
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    placeholder="Bank/UTR reference of the refund (optional)"
                    className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      disabled={isMutating}
                      onClick={() => onConfirmRefund(record.id, draft.trim() || undefined)}
                      className="rounded-lg bg-red-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {REFUND_CONFIRM_LABEL} · {formatRupeesExact(a.borrowerTotalPaise)}
                    </button>
                    <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenForm('close_refund')}
                  className="rounded-lg bg-red-600 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-red-700"
                >
                  Mark refunded ({formatRupeesExact(a.borrowerTotalPaise)})
                </button>
              )
            ) : null}

            {canCloseAsNoPayment ? (
              openForm === 'no_payment' ? (
                <div className="rounded-lg border border-[var(--color-divider)] p-3">
                  <textarea
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    placeholder="What the bank shows (the borrower is shown this)"
                    className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                    rows={2}
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      disabled={isMutating || draft.trim().length < 3}
                      onClick={() => onNoPaymentReceived(record.id, draft.trim())}
                      className="rounded-lg bg-[var(--color-ink)] px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
                    >
                      Confirm — nothing to refund
                    </button>
                    <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => onOpenForm('no_payment')} className={secondaryBtn}>
                  {REFUND_NOT_RECEIVED_LABEL}
                </button>
              )
            ) : null}

            {canSettle ? (
              openForm === 'settle' ? (
                <div className="rounded-lg border border-[var(--color-divider)] p-3">
                  <input
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    placeholder="Bank/UTR reference of the transfer"
                    className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      disabled={isMutating || draft.trim().length < 3}
                      onClick={() => onSettle(record.id, draft.trim())}
                      className="rounded-lg bg-brand-500 px-3 py-2 text-[12px] font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                      Confirm settled · {formatRupeesExact(a.ownerPayoutPaise)}
                    </button>
                    <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenForm('settle')}
                  className="rounded-lg bg-brand-500 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-brand-600"
                >
                  Mark settled ({formatRupeesExact(a.ownerPayoutPaise)})
                </button>
              )
            ) : null}

            {canRefund ? (
              openForm === 'refund' ? (
                <div className="rounded-lg border border-[var(--color-divider)] p-3">
                  <input
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    placeholder="Refund reference (optional)"
                    className="w-full rounded-lg border border-[var(--color-divider)] p-2 text-[12px] outline-none focus:border-brand-300"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      disabled={isMutating}
                      onClick={() => onRefundDeposit(record.id, draft.trim() || undefined)}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Confirm deposit refund · {formatRupeesExact(a.depositPaise)}
                    </button>
                    <button type="button" onClick={() => onOpenForm(null)} className={secondaryBtn}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => onOpenForm('refund')} className={secondaryBtn}>
                  Refund deposit ({formatRupeesExact(a.depositPaise)})
                </button>
              )
            ) : null}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default PaymentDetailDrawer;
